import { useMemo } from 'react';

import RadialDisc from './RadialDisc';
import { drawMosaicArcs } from './DrawDisc';
import { rgbaFromHex } from './discGeometry';

// Visual policy for the SKILLS disc. Module-level (pure, depend only on the
// node) so identities are stable and don't churn RadialDisc's render effect.
//
// Mainline history: the "disc paint honesty" fix (7f6810a) gave assessed
// branches a whisper-tint floor so assessed-but-developing and never-graded
// read differently at rest ("class averaged 2.9 everywhere" used to render
// white, identical to never-graded, truth gated behind the hover popover).
// ON THIS BRANCH that floor is deliberately neutralized — see the
// ASSESSED_FLOOR note below. Only the outer ring (Skill, depth 3) is
// clickable.
const UNASSESSED_FILL = '#ffffff';
// COMPARISON BRANCH (disc-v1-mosaic): historical pre-7f6810a rendering
// restored. The whisper-tint floor above (0.15, "disc paint honesty") landed
// in 7f6810a on 2026-07-08 — the SAME DAY the mosaic was retired (2c605b6) —
// so the mosaic users actually lived with painted assessed-but-zero-developed
// arcs WHITE, exactly what this page's restored v1 legend says ("White = none
// developed or not assessed"). Floor neutralized so the demo shows the
// lived-with disc, not the same-day parting-gift fix nobody used. NOTE: the
// developed threshold is DEV_MIN (DrawDisc MIN_DEVELOPED_SCORE; re-ruled
// 2026-07-31 to >= 3, which now MATCHES the historical mosaic's bar of 3).
// "Developed" is distinct from "proficient" (still level 4); the comparison
// holds the developed definition constant across disc designs.
const ASSESSED_FLOOR = 0;
// Perceptual ramp: linear alpha starves low mastery of pigment (0.2 mastery is
// nearly invisible), especially now that the old colored wireframe strokes —
// which added artificial color to every tile — are gone. sqrt keeps the order
// strictly monotone and full mastery fully saturated, but gives the low end
// visible presence (0.25 → 0.5, 0.5 → 0.71).
const presence = (a) => Math.max(ASSESSED_FLOOR, Math.sqrt(Math.max(0, a ?? 0)));
// Historical white must be PAINTED white, not rgba(color, 0) — a transparent
// arc reads as the card background in dark mode, not as the legend's white.
const isWhite = (d) => !d.assessed || !(d.alpha > 0);
// DEMO: `reveal` (0..1) is the bloom progress after the first grades land.
// Rings colour in from the centre outwards — Area first, then Competency,
// then Skill — each over ~56% of the timeline, overlapping. At reveal = 1 the
// fill is exactly the prototype's. A fill below 2% presence stays painted
// white so the bloom starts from the blank disc, not from a faint tint.
const easeOut = (t) => 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
const ringStage = (reveal, depth) => easeOut((reveal - ((depth ?? 1) - 1) * 0.22) / 0.56);
const fill = (d, reveal = 1) => {
  if (d.depth === 0) return 'transparent';
  if (isWhite(d)) return UNASSESSED_FILL;
  const a = presence(d.alpha) * (reveal >= 1 ? 1 : ringStage(reveal, d.depth));
  if (a < 0.02) return UNASSESSED_FILL;
  return rgbaFromHex(d.color || '#cbd5e1', a);
};
// White tiles stay WHITE under hover/selection — pigment is data, and a
// tinted selection on a white tile read exactly like a low-mastery fill.
// With the floor gone that now covers assessed-but-zero-developed arcs too:
// the +0.05/+0.08 lift would otherwise re-tint them under the cursor.
// The renderer's ring + shadow carry the selection; the fill stays honest.
const hoverFill = (d) =>
  isWhite(d) ? UNASSESSED_FILL : rgbaFromHex(d.color, Math.min(1, presence(d.alpha) + 0.05));
const selectedFill = (d) =>
  isWhite(d) ? UNASSESSED_FILL : rgbaFromHex(d.color, Math.min(1, presence(d.alpha) + 0.08));
const isClickable = (d) => d.depth === 3;
// Areas and competencies zoom the disc (Mosaico parity); skills open the sidebar.
const isZoomable = (d) => d.depth === 1 || d.depth === 2;

/**
 * @typedef {Object} DiscProps
 * @property {Array<Object>} curriculum   Curriculum tree (Area → Competency → Skill). Required.
 * @property {Array<Object>} [scores]     Flat score rows folded into the tree by drawMosaicArcs (default []).
 * @property {string} [kind]              Disc kind forwarded to drawMosaicArcs (default 'content').
 * @property {number} [width]             SVG width in px.
 * @property {number} [height]            SVG height in px.
 * @property {number} [gapByLevel]        Angular gap between arcs per depth (default 0.004).
 * @property {(node: ?Object) => void} [onHover]   Hovered node payload, or null on leave.
 * @property {(node: ?Object) => void} [onClick]   Clicked Skill node (incl. children + specificSkills).
 * @property {import('react').ComponentType<Object>} [popoverComponent]  Rendered inside the hover popover.
 * @property {number} [reveal]            0..1 bloom progress for the fill (demo); default 1 = fully painted.
 */

/**
 * Mosaico-style skills disc (3 rings): Area → Competency → Skill.
 * Hover shows a popover; clicking an Area or Competency zooms the disc into
 * it (click again or the background to zoom out); clicking a Skill (outer
 * cells) emits the node (with its children + specificSkills) for the sidebar.
 *
 * @param {DiscProps} props
 */
export default function Disc({
  curriculum,
  scores = [],
  // No default kind: the CLASS tree's nodes carry no top-level `kind`, so a
  // default of 'content' would filter every area out. The student page passes
  // kind="content" explicitly (its nodes do carry it).
  kind,
  width = 560,
  height = 560,
  gapByLevel = 0.004,
  onHover,
  onClick,
  popoverComponent,
  reveal = 1,
}) {
  const root = useMemo(
    () => drawMosaicArcs(curriculum || [], scores || [], kind, false, '', '', gapByLevel ?? 0.004),
    [curriculum, scores, kind, gapByLevel]
  );

  return (
    <RadialDisc
      root={root}
      width={width}
      height={height}
      onHover={onHover}
      onClick={onClick}
      popoverComponent={popoverComponent}
      fill={fill}
      hoverFill={hoverFill}
      selectedFill={selectedFill}
      isClickable={isClickable}
      isZoomable={isZoomable}
      reveal={reveal}
      popoverWidth={280}
      errorLabel="Disc not available"
    />
  );
}

